import { useState, useCallback } from 'react';
import { inventoryService } from '../services/inventoryService';

/**
 * Custom hook for inventory operations
 */
export const useInventory = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const addInventory = async (lotData) => {
    setLoading(true);
    setError(null);
    try {
      const result = await inventoryService.addInventory(lotData);
      return result;
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to add inventory');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const removeInventory = async (removalData) => {
    setLoading(true);
    setError(null);
    try {
      const result = await inventoryService.removeInventory(removalData);
      return result;
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to remove inventory');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const getItemLots = useCallback(async (itemId, includeEmpty = false) => {
    setLoading(true);
    setError(null);
    try {
      const data = await inventoryService.getItemLots(itemId, includeEmpty);
      return data;
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to fetch lots');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const getLots = useCallback(async (params = {}) => {
    setLoading(true);
    setError(null);
    try {
      const data = await inventoryService.getLots(params);
      return data;
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to fetch lots');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const getTransactions = useCallback(async (filters = {}) => {
    setLoading(true);
    setError(null);
    try {
      const result = await inventoryService.getTransactions(filters);
      return result; // Returns { data, total }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to fetch transactions');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Deliberately does not touch the shared `loading`/`error` state above:
  // callers (e.g. the ledger footer) fetch this in parallel with
  // getTransactions and need independent loading/error tracking so one
  // request finishing doesn't clear the other's in-flight indicator.
  const getTransactionSummary = useCallback(async (filters = {}) => {
    return inventoryService.getTransactionSummary(filters);
  }, []);

  // Also deliberately independent of the shared `loading`/`error` state
  // (same rationale as getTransactionSummary above): the export button
  // tracks its own loading/error state so it isn't affected by, and doesn't
  // affect, the table's fetch state.
  const exportTransactions = useCallback(async (filters = {}) => {
    return inventoryService.exportTransactions(filters);
  }, []);

  const getSummary = useCallback(async (skip = 0, limit = 50, search = null, category = null) => {
    setLoading(true);
    setError(null);
    try {
      const result = await inventoryService.getSummary(skip, limit, search, category);
      return result; // Returns { data, total }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to fetch inventory summary');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const getValueReport = useCallback(async (search = null, category = null) => {
    setLoading(true);
    setError(null);
    try {
      const data = await inventoryService.getValueReport(search, category);
      return data;
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to fetch value report');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    loading,
    error,
    addInventory,
    removeInventory,
    getItemLots,
    getLots,
    getTransactions,
    getTransactionSummary,
    exportTransactions,
    getSummary,
    getValueReport
  };
};
